import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Alert,
} from '@mui/material';
import { fetchActiveUsersDirectory } from '../../services/userDirectory.service';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Same identity-key shape used by StudyTeamForm's duplicate-detection (userId, else email). */
function identityKeys(person) {
  const keys = new Set();
  if (person?.userId != null && person.userId !== '') keys.add(`uid:${String(person.userId)}`);
  const em = normalizeEmail(person?.email);
  if (em) keys.add(`em:${em}`);
  return keys;
}

/**
 * Reusable "reassign a person" dialog — pick a replacement from the role-scoped
 * candidate list (people whose Hub resource_access grant maps to this role for
 * this study) for a given role, guarding against picking someone already in that
 * role. Shared by StudyTeamForm.jsx and OnlyOfficeWorkflow.jsx so the picker UX
 * and duplicate-guard logic live in exactly one place.
 */
export default function ReassignPersonDialog({
  open,
  onClose,
  projectId,
  roleKey,
  roleLabel,
  currentPerson,
  occupiedKeys,
  onConfirm,
}) {
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setError('');
    if (!projectId || !roleKey) {
      setDirectoryUsers([]);
      return;
    }
    setLoadingDirectory(true);
    fetchActiveUsersDirectory(projectId)
      .then((roster) => setDirectoryUsers(roster[roleKey] || []))
      .catch((e) => setError(e.message || 'Failed to load users'))
      .finally(() => setLoadingDirectory(false));
  }, [open, projectId, roleKey]);

  const options = directoryUsers.filter((u) => {
    const keys = identityKeys({ userId: u.id, email: u.email });
    for (const k of keys) {
      if (occupiedKeys?.has(k)) return false;
    }
    return true;
  });

  const handleConfirm = async () => {
    if (!selected) {
      setError('Select a replacement person first.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({
        name: selected.name || '',
        email: selected.email || '',
        userId: selected.id,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to reassign');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reassign {roleLabel}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Replacing <strong>{currentPerson?.name || currentPerson?.email || 'this person'}</strong> as{' '}
          {roleLabel}. The new person will start with no progress recorded; the previous person's history is
          kept.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Autocomplete
          size="small"
          loading={loadingDirectory}
          options={options}
          value={selected}
          onChange={(_, opt) => setSelected(opt)}
          getOptionLabel={(opt) => (opt?.email ? `${opt.name || '(no name)'} (${opt.email})` : opt?.name || '')}
          isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
          renderInput={(params) => (
            <TextField {...params} autoFocus label="Select replacement" placeholder="Search by name or email" />
          )}
        />
        {!loadingDirectory && !error && options.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No candidates found for this study/role in the IAM Hub directory yet — this list is populated from
            each user's Hub-side access grant, not from who's already listed on this study team. Close this
            dialog and use the role's own "Select user" field on the Study Team page instead, or add the
            replacement by name and email there directly.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={submitting || !selected}>
          Reassign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
